import { useState } from "react";

import { PaymentMethod } from "common/components/PaymentMethod";
import PoweredByStripe from "common/components/PoweredByStripe";
import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import { DialogFooter, DialogTitle } from "common/components/ui/Dialog";
import { DrawerFooter, DrawerTitle } from "common/components/ui/Drawer";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useLicenseMutations from "common/datahooks/useLicenseMutations";
import usePaymentSource from "common/datahooks/usePaymentSource";
import useSubscriptionMutations from "common/datahooks/useSubscriptionMutations";
import { trackMetaPixel } from "common/helpers/utils";
import useSubscriptionPricing from "common/hooks/useSubscriptionPricing";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type {
  PaymentSource,
  PricingPeriodUnit,
  Subscription,
} from "common/types";

import PrimaryPaymentSource from "../../PrimaryPaymentSource";
import SubscriptionPlan from "../SubscriptionPlan";
import PricingInfo from "./PricingInfo";

interface SummaryProps {
  licenseCount: number;
  selectedPeriodUnit: PricingPeriodUnit;
  accountsToCancel: string[];
  goToSuccess: () => void;
  setSelectedPaymentSource: (newPaymentSource: PaymentSource) => void;
  goBack: () => void;
  paymentSource: PaymentSource;
  accountIds?: string[];
}

export default function Summary({
  licenseCount,
  selectedPeriodUnit,
  accountsToCancel,
  goToSuccess,
  setSelectedPaymentSource,
  goBack,
  accountIds = null,
  paymentSource,
}: SummaryProps) {
  const [isEditingPaymentSource, setIsEditingPaymentSource] = useState(false);

  const {
    subscription,
    role,
    lifetime_license_count: lifetimeLicenseCount,
    license_count: workspaceLicenseCount,
  } = useSelectedWorkspaceContext();
  const { paymentSource: primaryPaymentSource, isLoadingPaymentSource } =
    usePaymentSource(subscription?.id);
  const { plan } = useSubscriptionPricing(selectedPeriodUnit, "user");

  const {
    setLicenseCount,
    isSettingLicenseCount,
    assignLicenses,
    isAssigningLicenses,
  } = useLicenseMutations();
  const { createSubscription, isCreatingSubscription } =
    useSubscriptionMutations();

  const isDesktop = useTwBreakpoint("lg");
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Title = isDesktop ? DialogTitle : DrawerTitle;

  const selectedPaymentSource = primaryPaymentSource ?? paymentSource;

  const isLifetimeDeal = lifetimeLicenseCount > 0;
  const isDowngrade = licenseCount < workspaceLicenseCount;
  const shouldAddPaymentSource =
    !isLoadingPaymentSource && !selectedPaymentSource && !isDowngrade;

  function getButtonText() {
    if (shouldAddPaymentSource) {
      return "Add payment method";
    }

    if (
      (!subscription && selectedPeriodUnit === "year") ||
      subscription?.status === "cancelled"
    ) {
      return "Pay and subscribe";
    }

    if (!!subscription || isLifetimeDeal) {
      return "Update subscription";
    }

    return "Activate subscription";
  }

  function handleLicenseUpdate() {
    const updateOrCreateSubscription = subscription
      ? setLicenseCount({
          count: licenseCount,
          planId: plan.id,
          accountsToCancel,
        })
      : createSubscription({
          count: licenseCount,
          planId: plan.id,
          type: "user",
          paymentSourceId: selectedPaymentSource.id,
        });

    updateOrCreateSubscription
      .then((response) => {
        const eventData = {
          value: plan.price * licenseCount,
          currency: "USD",
          content_type: "subscription",
          content_ids: [
            subscription ? subscription.id : (response as Subscription).id, // For new subscriptions, we'll get the subscription id from the response
          ],
          num_items: licenseCount,
        };

        trackMetaPixel("track", "Subscribe", eventData);
        trackMetaPixel("track", "Purchase", eventData);

        if (accountIds) {
          assignLicenses({ accountIds });
        }
      })
      .then(goToSuccess);
  }

  const isActionPending =
    isSettingLicenseCount || isCreatingSubscription || isAssigningLicenses;

  function addPaymentSource(newPaymentSource: PaymentSource) {
    setSelectedPaymentSource(newPaymentSource);
    setIsEditingPaymentSource(false);
  }

  if (isEditingPaymentSource)
    return (
      <PaymentMethod
        subscriptionId={subscription?.id}
        paymentSource={selectedPaymentSource}
        onBack={() => setIsEditingPaymentSource(false)}
        onSelect={subscription ? undefined : setSelectedPaymentSource}
        onNext={subscription ? undefined : addPaymentSource}
      />
    );

  return (
    <>
      <Title className="mb-8">Summary</Title>

      <SubscriptionPlan
        plan="outreach"
        pricingPeriodInfo={{ period: 1, period_unit: selectedPeriodUnit }}
      />

      <div className="flex flex-col gap-6 rounded-3xl bg-black-50 p-4 mt-4">
        <PrimaryPaymentSource
          isLoading={isLoadingPaymentSource}
          paymentSource={selectedPaymentSource}
          onEdit={() => setIsEditingPaymentSource(true)}
        />

        <PricingInfo
          subscription={subscription}
          currentCount={workspaceLicenseCount}
          newCount={licenseCount}
          selectedPeriodUnit={selectedPeriodUnit}
          type="user"
        />
      </div>
      <PoweredByStripe />

      <Footer className="mt-6">
        <RenderIf condition={!accountIds}>
          <Button
            variant="secondary-black"
            onClick={goBack}
            disabled={isActionPending}
          >
            Back
          </Button>
        </RenderIf>
        <Button
          className="ml-auto"
          disabled={shouldAddPaymentSource && role !== "owner"}
          onClick={
            shouldAddPaymentSource
              ? () => setIsEditingPaymentSource(true)
              : handleLicenseUpdate
          }
          isLoading={isActionPending || isLoadingPaymentSource}
        >
          {getButtonText()}
        </Button>
      </Footer>
    </>
  );
}
